/* <predictive-search>: live suggestions in the header search modal.
   Markup lives in sections/header.liquid (input #SearchModalInput, results #PredictiveSearchResults);
   result HTML comes from sections/predictive-search.liquid via the Section Rendering API.
   Keyboard: Arrow Up/Down move through options, Enter follows the selected option,
   Escape clears suggestions (a second Escape closes the dialog natively). */

(() => {
  if (customElements.get('predictive-search')) return;

  const LISTBOX_ID = 'predictive-search-results-list';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled !== 'true') return;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('#PredictiveSearchResults');
      if (!this.input || !this.results) return;

      this.cache = new Map();
      this.controller = null;
      this.query = '';

      /* Announce a short count instead of letting the whole listbox be read as a live region. */
      this.results.removeAttribute('aria-live');
      this.status = document.createElement('p');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('role', 'status');
      this.appendChild(this.status);

      const debounce = (window.theme && window.theme.debounce) || ((fn, ms) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn(...args), ms);
        };
      });
      this.onInput = debounce(() => this.search(this.input.value.trim()), 200);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.input.closest('form')?.addEventListener('submit', (event) => this.onSubmit(event));
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    get selected() {
      return this.results.querySelector('[role="option"][aria-selected="true"]');
    }

    async search(query) {
      this.query = query;
      if (!query) {
        this.clear();
        return;
      }

      if (this.cache.has(query)) {
        this.show(this.cache.get(query));
        return;
      }

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page',
        'resources[limit]': '4',
        section_id: 'predictive-search',
      });
      const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';

      try {
        const response = await fetch(`${base}?${params.toString()}`, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const html = section ? section.innerHTML : text;
        this.cache.set(query, html);
        if (this.query === query) this.show(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.clear();
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    show(html) {
      this.results.innerHTML = html;
      const listbox = this.results.querySelector(`#${LISTBOX_ID}`);
      if (!listbox) {
        this.clear();
        return;
      }
      this.input.setAttribute('aria-expanded', 'true');
      this.input.setAttribute('aria-controls', LISTBOX_ID);
      this.input.removeAttribute('aria-activedescendant');
      this.status.textContent = listbox.dataset.status || '';
    }

    clear() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.results.innerHTML = '';
      this.status.textContent = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', 'PredictiveSearchResults');
      this.input.removeAttribute('aria-activedescendant');
    }

    isOpen() {
      return this.input.getAttribute('aria-expanded') === 'true';
    }

    select(option) {
      this.options.forEach((el) => el.setAttribute('aria-selected', el === option ? 'true' : 'false'));
      if (option) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    move(step) {
      const options = this.options;
      if (!options.length) return;
      const index = options.indexOf(this.selected);
      let next = index + step;
      if (index === -1) next = step > 0 ? 0 : options.length - 1;
      if (next < 0 || next >= options.length) {
        this.select(null);
        return;
      }
      this.select(options[next]);
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          if (!this.isOpen()) return;
          event.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          if (!this.isOpen()) return;
          event.preventDefault();
          this.move(-1);
          break;
        case 'Escape':
          if (!this.isOpen()) return;
          /* Clear the query too: a search input with text would otherwise swallow the
             next Escape (native clear) instead of letting the dialog close. */
          event.preventDefault();
          event.stopPropagation();
          this.input.value = '';
          this.query = '';
          this.clear();
          break;
        default:
      }
    }

    onSubmit(event) {
      const option = this.isOpen() && this.selected;
      if (!option) return;
      const link = option.querySelector('a[href]');
      if (!link) return;
      event.preventDefault();
      link.click();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
